/**
 * MANDATORY: All monetary displays in the Connect portal MUST use
 * formatLocalAmount() or formatLocalRate() from this hook (or formatRate from useCountryRates).
 * DO NOT use hardcoded '$', '¢', or manual (cents / 100).toFixed(2).
 * Violations will cause the currency toggle to break.
 *
 * Resolution order (first hit wins, applied SYNCHRONOUSLY on mount so the
 * UI never flashes a wrong currency):
 *   1. Manual override          (localStorage `elefa_currency_override`)
 *   2. Last resolved country    (localStorage `elefa_resolved_country`)  ← persisted across sessions
 *   3. Phone-derived country    (sessionStorage `elefa_phone_country`)
 *   4. IP-cached country        (sessionStorage `elefa_caller_country`)
 *   5. Default 'AU'             (store currency - avoids USD flash on first launch)
 *
 * Background tasks then refine the value (native device locale, profile phone,
 * IP geolocation) and persist the final answer so the NEXT launch is instant.
 */
import { useState, useEffect, useCallback } from 'react';
import { DISPLAY_CURRENCIES } from '@/config/pricing/exchange-rates';
import { supabase } from '@/integrations/supabase/client';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { Capacitor } from '@capacitor/core';
import { Device } from '@capacitor/device';
// useFxRates intentionally NOT imported — display values are already AUD.

const SESSION_KEY = 'elefa_caller_country';
const OVERRIDE_KEY = 'elefa_currency_override';
const PHONE_COUNTRY_KEY = 'elefa_phone_country';
const RESOLVED_KEY = 'elefa_resolved_country';
// USD is the store currency — first paint shows US$ (no conversion math, no
// false AUD flash). The IP / locale detectors then refine to local currency.
const FIRST_LAUNCH_DEFAULT = 'US';

interface CallerCountryState {
  callerCountry: string; // ISO2
  isLoading: boolean;
}

function resolveInitialCountry(): string {
  try {
    const override = localStorage.getItem(OVERRIDE_KEY);
    if (override && DISPLAY_CURRENCIES[override]) return override;
    const resolved = localStorage.getItem(RESOLVED_KEY);
    if (resolved && DISPLAY_CURRENCIES[resolved]) return resolved;
    const phoneCountry = sessionStorage.getItem(PHONE_COUNTRY_KEY);
    if (phoneCountry && DISPLAY_CURRENCIES[phoneCountry]) return phoneCountry;
    const cached = sessionStorage.getItem(SESSION_KEY);
    if (cached && DISPLAY_CURRENCIES[cached]) return cached;
  } catch {
    /* storage unavailable */
  }
  return FIRST_LAUNCH_DEFAULT;
}

function persistResolved(country: string) {
  try {
    localStorage.setItem(RESOLVED_KEY, country);
  } catch {
    /* ignore */
  }
}

export function useCallerCountry() {
  const [state, setState] = useState<CallerCountryState>(() => ({
    callerCountry: resolveInitialCountry(),
    isLoading: false, // synchronous - never block the UI on first paint
  }));

  // Native device-locale detection (Capacitor) - runs only on first launch
  // (when no override / resolved / phone country has been persisted yet).
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    if (localStorage.getItem(OVERRIDE_KEY)) return;
    if (localStorage.getItem(RESOLVED_KEY)) return;

    (async () => {
      try {
        const { value } = await Device.getLanguageTag(); // e.g. "en-AU"
        const region = value?.split('-')[1]?.toUpperCase();
        if (region && DISPLAY_CURRENCIES[region]) {
          persistResolved(region);
          if (!localStorage.getItem(OVERRIDE_KEY)) {
            setState({ callerCountry: region, isLoading: false });
            window.dispatchEvent(new CustomEvent('elefa-currency-changed', { detail: region }));
          }
        }
      } catch { /* ignore */ }
    })();
  }, []);

  // Detect country from authenticated user's phone number (once per session)
  useEffect(() => {
    if (sessionStorage.getItem(PHONE_COUNTRY_KEY)) return;

    const detectPhoneCountry = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) return;

      const { data: profile } = await supabase
        .from('profiles')
        .select('phone_number')
        .eq('user_id', session.user.id)
        .single();

      if (profile?.phone_number) {
        const parsed = parsePhoneNumberFromString(profile.phone_number);
        if (parsed?.country) {
          const countryISO = parsed.country as string;
          sessionStorage.setItem(PHONE_COUNTRY_KEY, countryISO);
          persistResolved(countryISO);
          // Phone country trumps device locale / IP, but never an explicit user override
          if (!localStorage.getItem(OVERRIDE_KEY)) {
            setState({ callerCountry: countryISO, isLoading: false });
            window.dispatchEvent(new CustomEvent('elefa-currency-changed', { detail: countryISO }));
          }
        }
      }
    };
    detectPhoneCountry();
  }, []);

  // IP detection fallback - only when nothing better is known yet.
  // Goes through our `geo-detect` edge function (server-side IP read + cache)
  // so we don't burn the browser-side ipapi.co quota on every page load.
  useEffect(() => {
    if (localStorage.getItem(OVERRIDE_KEY)) return;
    if (localStorage.getItem(RESOLVED_KEY)) return;
    if (sessionStorage.getItem(PHONE_COUNTRY_KEY)) return;
    if (sessionStorage.getItem(SESSION_KEY)) return;
    if (Capacitor.isNativePlatform()) return; // device locale handles native

    let cancelled = false;
    (async () => {
      try {
        const { data } = await supabase.functions.invoke('geo-detect');
        if (cancelled) return;
        const country = (data as { country?: string | null })?.country;
        if (!country || !DISPLAY_CURRENCIES[country]) return;
        sessionStorage.setItem(SESSION_KEY, country);
        persistResolved(country);
        if (!localStorage.getItem(OVERRIDE_KEY) && !sessionStorage.getItem(PHONE_COUNTRY_KEY)) {
          setState({ callerCountry: country, isLoading: false });
          window.dispatchEvent(new CustomEvent('elefa-currency-changed', { detail: country }));
        }
      } catch {
        /* keep current default */
      }
    })();

    return () => { cancelled = true; };
  }, []);

  // Listen for currency changes from other hook instances
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as string;
      if (detail && detail !== state.callerCountry) {
        setState({ callerCountry: detail, isLoading: false });
      }
    };
    window.addEventListener('elefa-currency-changed', handler);
    return () => window.removeEventListener('elefa-currency-changed', handler);
  }, [state.callerCountry]);

  const setCountryOverride = useCallback((countryISO2: string) => {
    localStorage.setItem(OVERRIDE_KEY, countryISO2);
    persistResolved(countryISO2);
    setState({ callerCountry: countryISO2, isLoading: false });
    window.dispatchEvent(new CustomEvent('elefa-currency-changed', { detail: countryISO2 }));
  }, []);

  // currencyConfig removed — display is always AUD (workstream A).
  void DISPLAY_CURRENCIES;

  // Rate + balance values arrive from the server already in AUD cents
  // (voice_routes.retail_rate_aud_inc_gst_cents / calling_credit AUD balance).
  // Per LOVABLE_EXECUTION_INSTRUCTIONS 2026-07-07 (workstream A), do NOT
  // apply any FX (no ×1.55, no useFxRates) — display as-is with A$ symbol
  // so the site matches the app dialpad rate and the billed amount.
  const convertFromAUD = useCallback((audCents: number): number => audCents, []);

  const formatLocalRate = useCallback((audCents: number): string => {
    return `A$${(audCents / 100).toFixed(2)}`;
  }, []);

  const formatLocalAmount = useCallback((audCents: number): string => {
    return `A$${(audCents / 100).toFixed(2)}`;
  }, []);

  return {
    callerCountry: state.callerCountry,
    displayCurrency: 'AUD',
    currencySymbol: 'A$',
    currencyName: 'Australian Dollars',
    showGst: false,
    convertFromAUD,
    formatLocalRate,
    formatLocalAmount,
    isLoading: state.isLoading,
    setCountryOverride,
  };
}
